import type { ProfileStats, RewardBundle } from '@vm/shared';
import { CAMOS } from '../cosmetics';

export interface AchievementDef {
  id: string;
  title: string;
  description: string;
  /** Key into an AchievementSnapshot (lifetime totals / bests). */
  metric: string;
  goal: number;
  reward: RewardBundle;
  /** Lucide icon name. */
  icon: string;
}

const a = (id: string, title: string, description: string, metric: string, goal: number, reward: RewardBundle, icon: string): AchievementDef => ({
  id,
  title,
  description,
  metric,
  goal,
  reward,
  icon,
});

export const ACHIEVEMENTS: AchievementDef[] = [
  a('first_takedown', 'First Takedown', 'Get your first takedown.', 'kills', 1, { coins: 100 }, 'crosshair'),
  a('kills_100', 'Centurion', 'Get 100 takedowns.', 'kills', 100, { coins: 500 }, 'crosshair'),
  a('kills_1000', 'Veteran Marine', 'Get 1,000 takedowns.', 'kills', 1000, { coins: 2000, gems: 25 }, 'swords'),
  a('kills_10000', 'Legend of the Arena', 'Get 10,000 takedowns.', 'kills', 10000, { coins: 10000, gems: 100 }, 'crown'),
  a('first_win', 'First Victory', 'Win a match.', 'wins', 1, { coins: 150 }, 'trophy'),
  a('wins_50', 'Seasoned Victor', 'Win 50 matches.', 'wins', 50, { coins: 1000, gems: 10 }, 'trophy'),
  a('wins_500', 'Conqueror', 'Win 500 matches.', 'wins', 500, { coins: 5000, gems: 50, items: [{ type: 'banner', id: 'champion' }] }, 'trophy'),
  a('matches_10', 'Enlisted', 'Finish 10 matches.', 'matches', 10, { coins: 200 }, 'flag'),
  a('matches_100', 'Regular', 'Finish 100 matches.', 'matches', 100, { coins: 1000, gems: 10 }, 'flag'),
  a('matches_1000', 'Lifer', 'Finish 1,000 matches.', 'matches', 1000, { coins: 5000, gems: 50, items: [{ type: 'banner', id: 'veteran' }] }, 'medal'),
  a('headshots_50', 'Steady Aim', 'Land 50 headshot takedowns.', 'headshots', 50, { coins: 500 }, 'target'),
  a('headshots_1000', 'Deadeye', 'Land 1,000 headshot takedowns.', 'headshots', 1000, { coins: 4000, gems: 40 }, 'target'),
  a('voxels_10k', 'Renovator', 'Destroy 10,000 voxels.', 'voxels', 10000, { coins: 300 }, 'hammer'),
  a('voxels_250k', 'Wrecking Crew', 'Destroy 250,000 voxels.', 'voxels', 250000, { coins: 2500, gems: 20 }, 'bomb'),
  a('voxels_2m', 'Force of Nature', 'Destroy 2,000,000 voxels.', 'voxels', 2000000, { coins: 8000, gems: 75 }, 'bomb'),
  a('built_5k', 'Builder', 'Build 5,000 voxels.', 'built', 5000, { coins: 800 }, 'brick-wall'),
  a('collapse_1', 'Timber!', 'Take down an enemy with a collapsing structure.', 'collapse', 1, { coins: 300 }, 'building'),
  a('collapse_50', 'Demolition Expert', 'Get 50 collapse takedowns.', 'collapse', 50, { coins: 3000, gems: 30 }, 'building'),
  a('streak_5', 'On a Roll', 'Reach a 5-takedown streak.', 'streak', 5, { coins: 300 }, 'flame'),
  a('streak_10', 'Unstoppable', 'Reach a 10-takedown streak.', 'streak', 10, { coins: 1000, gems: 10 }, 'flame'),
  a('streak_20', 'Voxel Storm', 'Reach a 20-takedown streak.', 'streak', 20, { coins: 3000, gems: 30 }, 'zap'),
  a('level_10', 'Corporal', 'Reach level 10.', 'level', 10, { coins: 500 }, 'star'),
  a('level_25', 'Sergeant', 'Reach level 25.', 'level', 25, { coins: 1500, gems: 15 }, 'star'),
  a('level_50', 'Lieutenant', 'Reach level 50.', 'level', 50, { coins: 4000, gems: 40 }, 'star'),
  a('level_100', 'Commander', 'Reach level 100.', 'level', 100, { coins: 10000, gems: 100 }, 'crown'),
  a('objectives_100', 'Mission Ready', 'Complete 100 objectives.', 'objectives', 100, { coins: 1500, gems: 10 }, 'flag-triangle-right'),
  a('zombies_500', 'Purifier', 'Clear 500 corrupted marines.', 'zombies', 500, { coins: 1500, gems: 10 }, 'biohazard'),
  a('waves_15', 'Last Stand', 'Survive 15 waves in one Corruption Survival match.', 'waves', 15, { coins: 2000, gems: 20 }, 'shield'),
  a('mastery_1', 'Specialist', 'Reach bronze mastery with a weapon.', 'mastery', 1, { coins: 500 }, 'award'),
  a('mastery_10', 'Arsenal Master', 'Reach bronze mastery with 10 weapons.', 'mastery', 10, { coins: 5000, gems: 50 }, 'award'),
  a('duel_wins_10', 'Duelist', 'Win 10 duels.', 'wins:duel', 10, { coins: 1500, gems: 10 }, 'crosshair'),
  a('br_win', 'Last Marine Standing', 'Win a Battle Royale match.', 'wins:br', 1, { coins: 1000, gems: 10 }, 'crown'),
  a('friends_5', 'Squad Up', 'Have 5 friends.', 'friends', 5, { coins: 500 }, 'users'),
  a('clan_join', 'Brothers in Arms', 'Join or create a clan.', 'clan', 1, { coins: 300 }, 'shield-half'),
  a('login_7', 'Dedicated', 'Claim the daily login reward 7 times.', 'logins', 7, { coins: 500, gems: 5 }, 'calendar'),
  a('login_30', 'Loyal Marine', 'Claim the daily login reward 30 times.', 'logins', 30, { coins: 2000, gems: 25 }, 'calendar-check'),
  a('playtime_10h', 'Time Well Spent', 'Play for 10 hours.', 'hours', 10, { coins: 1500, gems: 10 }, 'clock'),
  a('rating_1300', 'Rising Star', 'Reach a skill rating of 1300.', 'rating', 1300, { coins: 2000, gems: 20 }, 'trending-up'),
];

export const ACHIEVEMENT_BY_ID: Record<string, AchievementDef> = Object.fromEntries(ACHIEVEMENTS.map((x) => [x.id, x]));

/** Kills with one weapon needed for its first (bronze) mastery camo. */
export const MASTERY_KILLS: number = CAMOS.filter((c) => c.masteryKills).reduce((m, c) => Math.min(m, c.masteryKills!), Infinity) || 50;

export interface AchievementInput {
  stats: ProfileStats;
  level: number;
  objectives: number;
  zombiesKilled: number;
  bestWaves: number;
  modeWins: Record<string, number>;
  friends: number;
  inClan: boolean;
  loginDays: number;
}

/** Lifetime values for every achievement metric. */
export type AchievementSnapshot = Record<string, number>;

export function achievementSnapshot(i: AchievementInput): AchievementSnapshot {
  const s = i.stats;
  const snap: AchievementSnapshot = {
    kills: s.kills,
    wins: s.wins,
    matches: s.matches,
    headshots: s.headshots,
    voxels: s.voxelsDestroyed,
    built: s.voxelsBuilt,
    collapse: s.collapseKills,
    streak: s.bestStreak,
    level: i.level,
    objectives: i.objectives,
    zombies: i.zombiesKilled,
    waves: i.bestWaves,
    mastery: Object.values(s.weaponKills).filter((k) => k >= MASTERY_KILLS).length,
    friends: i.friends,
    clan: i.inClan ? 1 : 0,
    logins: i.loginDays,
    hours: Math.floor(s.playtimeSec / 3600),
    rating: s.rating,
  };
  for (const [mode, n] of Object.entries(i.modeWins)) snap[`wins:${mode}`] = n;
  return snap;
}

export function achievementProgress(def: AchievementDef, snap: AchievementSnapshot): number {
  return Math.max(0, Math.min(def.goal, Math.floor(snap[def.metric] ?? 0)));
}
